// Collection/search top bar: grid-view toggle (3 vs 4 columns, persisted) + the custom sort select.
// The chosen view lives as an attribute on the section root, so a facet AJAX refetch of the grid
// (or a pagination reload, which re-reads localStorage) never loses it.
(function () {
  const STORAGE_KEY = "theme:collection-grid-view";
  const SECTION_CLASSES = ["collection-section", "search-section"];

  class CollectionTopBar {
    constructor(section) {
      this.section = section;
      this.root = section.querySelector(".main-collection");
      this.controller = new AbortController();

      this.onToggleClick = this.onToggleClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onStickyScroll = this.onStickyScroll.bind(this);
      this.updateSortPlacement = this.updateSortPlacement.bind(this);

      const signal = this.controller.signal;

      this.toggle = section.querySelector("[data-grid-view-toggle]");
      if (this.toggle && this.root) {
        this.toggle.addEventListener("click", this.onToggleClick, { signal });
      }

      this.topBar = section.querySelector(".main-collection__top");
      if (this.topBar) {
        window.addEventListener("scroll", this.onStickyScroll, {
          passive: true,
          signal,
        });
        window.addEventListener("resize", this.onStickyScroll, {
          passive: true,
          signal,
        });
        // The bar's own `top` animates with the header, so the pinned test has to be
        // re-run when that settles and not only while the page is being scrolled.
        this.topBar.addEventListener("transitionend", this.onStickyScroll, {
          signal,
        });
        this.updateStickyBar();
      }

      this.sortSelects = Array.from(
        section.querySelectorAll("collection-sort-select")
      );
      this.sortSelects.forEach((element) =>
        this.initSortSelect(element, signal)
      );

      if (this.sortSelects.length) {
        document.addEventListener("click", this.onDocumentClick, { signal });
        document.addEventListener("keydown", this.onDocumentKeydown, {
          signal,
        });
      }

      // Below the sidebar breakpoint the sort control is hosted by the filter panel, which
      // leaves the narrow top bar with just the Filters button and the result count. The one
      // control is moved between the two hosts, so there is never a second sort_by input.
      this.sortSelect = this.sortSelects[0];
      this.sortBarHost = section.querySelector(".facets-sorting__form");
      this.sortPanelHost = section.querySelector("[data-facets-sort-slot]");
      this.sortDesktopQuery = window.matchMedia("(min-width: 990px)");

      if (this.sortSelect && this.sortBarHost && this.sortPanelHost) {
        this.sortDesktopQuery.addEventListener(
          "change",
          this.updateSortPlacement,
          { signal }
        );
        this.updateSortPlacement();
      }
    }

    destroy() {
      this.controller.abort();
      cancelAnimationFrame(this.stickyFrame);
    }

    onStickyScroll() {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        this.updateStickyBar();
      });
    }

    updateSortPlacement() {
      const host = this.sortDesktopQuery.matches
        ? this.sortBarHost
        : this.sortPanelHost;
      if (this.sortSelect.parentElement === host) return;

      // Its own dropdown geometry differs between the two hosts, so it never carries an
      // open panel across.
      this.setSortOpen(this.sortSelect, false);
      host.appendChild(this.sortSelect);
    }

    // The bar stays stuck to the top of the whole grid wrapper, so at the end of the
    // page it hangs over the pagination tail with no products left to sort. The grid is
    // looked up per run — a facet refetch replaces it.
    updateStickyBar() {
      const barRect = this.topBar.getBoundingClientRect();

      // Pinned = sitting on its own sticky offset rather than at its place in the flow.
      // Only then may the bar paint over the strip above it (see .is-stuck in the SCSS);
      // in the flow that strip is the quick filters.
      const offset = parseFloat(getComputedStyle(this.topBar).top) || 0;
      this.topBar.classList.toggle("is-stuck", barRect.top <= offset + 0.5);

      const grid = this.section.querySelector(".main-collection__list");
      if (!grid) {
        // Empty result: the bar is the only way back out of the filters.
        this.topBar.classList.remove("is-out-of-view");
        return;
      }

      const gridBottom = grid.getBoundingClientRect().bottom;

      this.topBar.classList.toggle("is-out-of-view", gridBottom <= barRect.bottom);
    }

    onToggleClick(event) {
      event.preventDefault();

      const current =
        this.root.getAttribute("data-grid-view") === "3" ? "3" : "4";
      const next = current === "4" ? "3" : "4";

      this.root.setAttribute("data-grid-view", next);

      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch (error) {
        // Storage unavailable (private mode) — the choice just doesn't persist.
      }
    }

    initSortSelect(element, signal) {
      const trigger = element.querySelector(".sort-select__trigger");
      const valueElement = element.querySelector("[data-sort-select-value]");
      if (!trigger) return;

      trigger.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.setSortOpen(element, !element.classList.contains("is-open"));
        },
        { signal }
      );

      // Arrow keys move the checked radio, so a keyboard change keeps the list open
      // (closing it would hide the focused option and drop focus to <body>); Enter
      // or Escape closes it back onto the trigger.
      let pointerChange = false;
      element.addEventListener(
        "pointerdown",
        () => {
          pointerChange = true;
        },
        { signal }
      );
      element.addEventListener(
        "keydown",
        () => {
          pointerChange = false;
        },
        { signal, capture: true }
      );

      element.querySelectorAll(".sort-select__input").forEach((input) => {
        input.addEventListener(
          "change",
          () => {
            if (valueElement && input.dataset.sortLabel) {
              valueElement.textContent = input.dataset.sortLabel;
              const prefix = element.querySelector(".sort-select__prefix");
              trigger.setAttribute(
                "aria-label",
                `${prefix?.textContent.trim() || ""} ${input.dataset.sortLabel}`.trim()
              );
            }
            if (pointerChange) this.setSortOpen(element, false);
            pointerChange = false;
          },
          { signal }
        );

        input.addEventListener(
          "keydown",
          (event) => {
            if (event.key !== "Enter") return;
            // Enter would otherwise submit the sort form natively
            event.preventDefault();
            this.setSortOpen(element, false);
            trigger.focus();
          },
          { signal }
        );
      });

      element.addEventListener(
        "focusout",
        (event) => {
          if (!element.classList.contains("is-open")) return;
          if (event.relatedTarget && element.contains(event.relatedTarget)) {
            return;
          }
          this.setSortOpen(element, false);
        },
        { signal }
      );
    }

    setSortOpen(element, open) {
      element.classList.toggle("is-open", open);

      const trigger = element.querySelector(".sort-select__trigger");
      if (trigger)
        trigger.setAttribute("aria-expanded", open ? "true" : "false");

      if (!open) return;

      const checked = element.querySelector(".sort-select__input:checked");
      if (checked) checked.focus({ preventScroll: true });
    }

    closeAllSortSelects(focusTrigger) {
      this.sortSelects.forEach((element) => {
        if (!element.classList.contains("is-open")) return;
        this.setSortOpen(element, false);
        if (!focusTrigger) return;
        element.querySelector(".sort-select__trigger")?.focus();
      });
    }

    onDocumentClick(event) {
      this.sortSelects.forEach((element) => {
        if (!element.classList.contains("is-open")) return;
        if (element.contains(event.target)) return;
        this.setSortOpen(element, false);
      });
    }

    onDocumentKeydown(event) {
      if (event.key !== "Escape") return;
      this.closeAllSortSelects(true);
    }
  }

  function initSection(section) {
    if (!section || !section.classList) return;
    if (!SECTION_CLASSES.some((name) => section.classList.contains(name))) {
      return;
    }

    if (section._collectionTopBar) {
      section._collectionTopBar.destroy();
      section._collectionTopBar = null;
    }

    section._collectionTopBar = new CollectionTopBar(section);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeCollectionTopBarEvents) {
    window.__themeCollectionTopBarEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const section = event.target;
      if (!section || !section._collectionTopBar) return;

      section._collectionTopBar.destroy();
      section._collectionTopBar = null;
    });
  }
})();
